/**
 * Uncanned cart client.
 *
 * Every add-to-cart surface on the storefront (PDP buy box, sticky bar, quick-add on
 * cards, cart upsell) goes through this one module, which guarantees the four things
 * the previous per-section fetch() calls each got wrong:
 *
 *   1. Shopify's 4xx responses are errors. `cart/add.js` answers 422 with a JSON body
 *      for sold-out or unavailable lines; the old code read that as success and told
 *      the customer "Added to Cart!" while nothing had been added.
 *   2. The header drawer and the cart count are updated. `cart:updated` is what
 *      sections/uncanned-header.liquid listens for; nothing was dispatching it.
 *   3. Analytics fire exactly once, from one place.
 *   4. Failures surface in the UI rather than an alert() or a silent console.error.
 */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function url(path) {
    return root.replace(/\/$/, '/') + path;
  }

  function parse(response) {
    return response.text().then(function (text) {
      var data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = null;
      }
      if (!response.ok) {
        var error = new Error(
          (data && (data.description || data.message)) ||
            'We could not update your cart. Please try again.'
        );
        error.status = response.status;
        error.body = data;
        throw error;
      }
      return data;
    });
  }

  function request(path, body) {
    return fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(parse);
  }

  /** Broadcast a fresh cart so the header, drawer and progress bar all agree. */
  function publish(cart, options) {
    window.Uncanned = window.Uncanned || {};
    window.Uncanned.cartSnapshot = cart;
    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: Object.assign({ cart: cart }, options || {}) })
    );
  }

  function getCart() {
    return fetch(url('cart.js'), { headers: { Accept: 'application/json' } }).then(parse);
  }

  var api = {
    getCart: getCart,

    /**
     * @param {{id: string|number, quantity?: number, properties?: object,
     *          selling_plan?: string|number}} line
     * @param {{openDrawer?: boolean, source?: string, product?: object}} [options]
     */
    add: function (line, options) {
      options = options || {};
      var quantity = Number(line.quantity) || 1;
      // A selling plan turns the line into a subscription. Only send the key when one is
      // actually chosen - passing null makes cart/add.js reject the whole request.
      var item = {
        id: String(line.id),
        quantity: quantity,
        properties: line.properties || undefined,
      };
      if (line.selling_plan) item.selling_plan = Number(line.selling_plan);
      return request('cart/add.js', { items: [item] })
        .then(function (result) {
          // cart/add.js returns the added lines, not the whole cart.
          var added = (result && result.items && result.items[0]) || result;
          if (window.Uncanned && window.Uncanned.track) {
            window.Uncanned.track.addToCart(
              Object.assign({}, added, { quantity: quantity }),
              { source: options.source, id: line.id, at: Date.now() }
            );
          }
          return getCart().then(function (cart) {
            publish(cart, { openDrawer: options.openDrawer !== false, source: options.source });
            return cart;
          });
        });
    },

    /** @param {string} key line item key @param {number} quantity 0 removes the line */
    change: function (key, quantity, options) {
      options = options || {};
      var previous = null;
      if (window.Uncanned && window.Uncanned.cartSnapshot) {
        previous = (window.Uncanned.cartSnapshot.items || []).filter(function (i) {
          return i.key === key;
        })[0];
      }
      return request('cart/change.js', { id: key, quantity: quantity }).then(function (cart) {
        if (previous && window.Uncanned && window.Uncanned.track) {
          var delta = previous.quantity - quantity;
          if (delta > 0) {
            window.Uncanned.track.removeFromCart(
              Object.assign({}, previous, { quantity: delta })
            );
          }
        }
        publish(cart, { openDrawer: false, source: options.source, silent: true });
        return cart;
      });
    },

    remove: function (key, options) {
      return api.change(key, 0, options);
    },
  };

  window.Uncanned = window.Uncanned || {};
  window.Uncanned.cart = api;

  /** Keep the snapshot warm so begin_checkout has line items to report. */
  document.addEventListener('DOMContentLoaded', function () {
    if (window.Uncanned.cartSnapshot) return;
    getCart()
      .then(function (cart) {
        window.Uncanned.cartSnapshot = cart;
      })
      .catch(function () {
        /* A cart read failing is not worth surfacing to the customer. */
      });
  });
})();
